import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Save, CreditCard, Building2 } from 'lucide-react';

export function BillingSettingsTab({ api }) {
  const [settings, setSettings] = useState({ fee_type: 'cc', cc_rate: 2.9, cc_flat: 0.30, ach_rate: 0.8, ach_cap: 5.00 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/billing/settings').then(res => {
      setSettings({ fee_type: 'cc', cc_rate: 2.9, cc_flat: 0.30, ach_rate: 0.8, ach_cap: 5.00, ...res.data });
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/billing/settings', settings);
      toast.success('Billing settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-bold text-[#0A1628] mb-4">Payment Processing Fee</h3>
        <p className="text-sm text-gray-500 mb-6">Choose which fee type to apply on quotes and invoices. This fee is added to the client total.</p>
        <div className="grid grid-cols-2 gap-4 mb-6">
          <FeeCard
            active={settings.fee_type === 'cc'}
            onClick={() => setSettings({ ...settings, fee_type: 'cc' })}
            icon={CreditCard}
            title="Credit Card"
            desc="Standard Stripe card processing"
            detail={`${settings.cc_rate}% + $${settings.cc_flat.toFixed(2)} per transaction`}
            color="blue"
          />
          <FeeCard
            active={settings.fee_type === 'ach'}
            onClick={() => setSettings({ ...settings, fee_type: 'ach' })}
            icon={Building2}
            title="ACH / Bank Transfer"
            desc="Lower cost bank transfers"
            detail={`${settings.ach_rate}% (capped at $${settings.ach_cap.toFixed(2)})`}
            color="green"
          />
        </div>

        <h4 className="font-semibold text-[#0A1628] mb-3">Custom Rates</h4>
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-3">
            <h5 className="text-sm font-medium text-gray-600">Credit Card Rates</h5>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Rate (%)</label>
                <input type="number" step="0.1" value={settings.cc_rate} onChange={(e) => setSettings({ ...settings, cc_rate: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Flat Fee ($)</label>
                <input type="number" step="0.01" value={settings.cc_flat} onChange={(e) => setSettings({ ...settings, cc_flat: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h5 className="text-sm font-medium text-gray-600">ACH Rates</h5>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Rate (%)</label>
                <input type="number" step="0.1" value={settings.ach_rate} onChange={(e) => setSettings({ ...settings, ach_rate: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Cap ($)</label>
                <input type="number" step="0.01" value={settings.ach_cap} onChange={(e) => setSettings({ ...settings, ach_cap: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg text-sm" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Terms */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-bold text-[#0A1628] mb-4">Payment Terms</h3>
        <p className="text-sm text-gray-500 mb-6">Signed quotes now show the full amount as <strong>Due Now</strong>. Monthly/yearly items bill again on the next billing cycle.</p>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 mb-4 text-sm text-blue-900">
          <p className="font-semibold mb-1">Default display</p>
          <p>Client-facing quote and signing pages now use:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1 text-blue-800">
            <li>Due Now</li>
            <li>Next Billing Cycle</li>
          </ul>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Payment Terms Note</label>
          <textarea value={settings.payment_terms_note || ''} onChange={(e) => setSettings({ ...settings, payment_terms_note: e.target.value })} placeholder="e.g., Payment is due within 30 days of invoice date. Late payments subject to 1.5% monthly interest." rows={3} className="w-full px-3 py-2 border rounded-lg text-sm resize-none" />
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-[#014DB7]"><Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Settings'}</Button>
      </div>
    </div>
  );
}

function FeeCard({ active, onClick, icon: Icon, title, desc, detail, color }) {
  const borderColor = active ? (color === 'blue' ? 'border-[#014DB7] bg-blue-50' : 'border-[#84CC16] bg-green-50') : 'border-gray-200';
  return (
    <button onClick={onClick} className={`p-5 rounded-xl border-2 text-left transition-all ${borderColor}`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${active ? (color === 'blue' ? 'bg-[#014DB7]' : 'bg-[#84CC16]') : 'bg-gray-200'}`}>
          <Icon className={`w-5 h-5 ${active ? 'text-white' : 'text-gray-500'}`} />
        </div>
        <div>
          <p className="font-semibold text-[#0A1628]">{title}</p>
          <p className="text-xs text-gray-500">{desc}</p>
        </div>
      </div>
      <p className="text-sm font-medium text-gray-600">{detail}</p>
    </button>
  );
}
